// Timeline event operational pattern (DESIGN_SYSTEM.md §10.9, WORKSPACE_COCKPIT_SPEC.md §9.5).
// Append-only event indicator with state glyphs, tabular timestamps, actor attribution,
// step references, evidence citations, and reversal event indicators.

import { type KeyboardEvent, type MouseEvent } from 'react';
import { Eye, FileText, RotateCcw } from 'lucide-react';
import type { RunState } from './TruthLabel.tsx';
import styles from './TimelineEvent.module.css';

export interface TimelineEventProps {
  id: string;
  at: string;
  actor: string;
  text: string;
  state?: RunState;
  stepId?: string;
  evidenceId?: string;
  findingId?: string;
  durationMs?: number;
  isReversal?: boolean;
  isSelected?: boolean;
  onInspect?: (id: string) => void;
  onSelectEvidence?: (evidenceId: string) => void;
  onSelect?: (id: string) => void;
}

const RUN_GLYPH: Record<RunState, string> = {
  PROPOSED: '○',
  AWAITING_APPROVAL: '◷',
  READY: '▷',
  RUNNING: '▶',
  QUEUED: '◷',
  COMPLETED: '✓',
  PARTIAL: '◐',
  BLOCKED: '⚿',
  CANCELLED: '■',
  STOP_REQUESTED: '■',
  FAILED: '✕',
  STALLED: '!',
};

export function TimelineEvent({
  id,
  at,
  actor,
  text,
  state = 'COMPLETED',
  stepId,
  evidenceId,
  findingId,
  durationMs,
  isReversal = false,
  isSelected = false,
  onInspect,
  onSelectEvidence,
  onSelect,
}: TimelineEventProps) {
  const handleRowClick = (e: MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    onSelect?.(id);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(id);
    }
  };

  const handleEvidenceClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (evidenceId) onSelectEvidence?.(evidenceId);
  };

  const handleInspectClick = (e: MouseEvent) => {
    e.stopPropagation();
    onInspect?.(id);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="listitem"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      aria-label={`Event at ${at}: ${text}`}
      data-selected={isSelected ? 'true' : undefined}
      data-state={state}
      data-reversal={isReversal ? 'true' : undefined}
      className={styles.eventRow}
      onClick={handleRowClick}
      onKeyDown={handleKeyDown}
    >
      {/* State spine & glyph */}
      <div className={styles.markerContainer}>
        <span className={styles.glyph} aria-hidden="true">
          {isReversal ? '↺' : RUN_GLYPH[state]}
        </span>
        <div className={styles.spine} aria-hidden="true" />
      </div>

      {/* Main event content */}
      <div className={styles.content}>
        <div className={styles.header}>
          <time className={styles.time} dateTime={at} title={at}>
            {at.includes('T') ? (at.split('T')[1] ?? '').replace('Z', '') : at}
          </time>
          <span className={styles.actor}>{actor}</span>
          {stepId && <code className={styles.stepId}>{stepId}</code>}
          {durationMs !== undefined && <span className={styles.duration}>{durationMs} ms</span>}
          {isReversal && (
            <span className={styles.reversalBadge}>
              <RotateCcw size={12} aria-hidden="true" />
              <span>Reversal</span>
            </span>
          )}
        </div>

        <p className={styles.text}>{text}</p>

        {/* References: evidence & finding citations */}
        {(evidenceId || findingId) && (
          <div className={styles.references}>
            {evidenceId && (
              <button
                type="button"
                className={styles.refLink}
                onClick={handleEvidenceClick}
                aria-label={`View evidence ${evidenceId}`}
              >
                <FileText size={12} aria-hidden="true" />
                <span>Evidence {evidenceId}</span>
              </button>
            )}
            {findingId && <span className={styles.refBadge}>Finding {findingId}</span>}
          </div>
        )}
      </div>

      {/* Inspect trigger */}
      {onInspect && (
        <button
          type="button"
          className={styles.inspectBtn}
          onClick={handleInspectClick}
          aria-label={`Inspect event ${id}`}
        >
          <Eye size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
